import {
  AfterViewInit,
  Directive,
  ElementRef,
  Host,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DropdownComponent } from '@appkit4/angular-components/dropdown';
import { Subscription } from 'rxjs';

/**
 * AppKit 4.34 ap-dropdown document mousedown (capture) treats the first
 * `.cdk-overlay-pane` as the panel. Nested overlays (MatDialog + dropdown)
 * then close when the search box or a later pane is clicked.
 *
 * scrollSelector only closes the panel when that ancestor scrolls — it does
 * not change which overlay pane is used for outside-click. This host
 * directive replaces the instance capture listener (not the prototype) and
 * adds a window focusin guard only while this dropdown is open.
 */
@Directive({
  selector: 'ap-dropdown',
  standalone: false,
})
export class AppkitDropdownOverlayClickDirective
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly subs = new Subscription();
  private captureMouseDown: EventListener | null = null;
  private focusinGuard: ((event: FocusEvent) => void) | null = null;

  constructor(
    @Host() private readonly dropdown: DropdownComponent,
    private readonly host: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.applyModalScrollSelector();
    this.subs.add(
      this.dropdown.onVisibleChange.subscribe((event: { dropdownStatus?: boolean }) => {
        if (event?.dropdownStatus) {
          this.attachFocusinGuard();
        } else {
          this.detachFocusinGuard();
        }
      })
    );
  }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      queueMicrotask(() => this.replaceInstanceCaptureListener());
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.detachFocusinGuard();
    this.detachCaptureMouseDown();
  }

  /**
   * Close when the modal body scrolls so the CDK panel does not detach from
   * the trigger. Does not change overlay attachment / outside-click targeting.
   */
  private applyModalScrollSelector(): void {
    if (this.dropdown.scrollSelector) {
      return;
    }
    const modalHost = this.host.nativeElement.closest(
      '.modal, .modal-dialog, .mat-dialog-container, .mat-mdc-dialog-container'
    );
    if (modalHost) {
      this.dropdown.scrollSelector =
        '.modal-body, .modal-content, .mat-dialog-content, .mat-mdc-dialog-content';
    }
  }

  private replaceInstanceCaptureListener(): void {
    const instance = this.dropdown as unknown as {
      documentClickUnlistener?: EventListener | null;
    };
    const previous = instance.documentClickUnlistener;
    if (typeof previous === 'function') {
      document.removeEventListener('mousedown', previous, true);
    }

    this.captureMouseDown = (event: Event) => this.handleCaptureMouseDown(event as MouseEvent);
    instance.documentClickUnlistener = this.captureMouseDown;
    document.addEventListener('mousedown', this.captureMouseDown, true);
  }

  private detachCaptureMouseDown(): void {
    if (!this.captureMouseDown) {
      return;
    }
    document.removeEventListener('mousedown', this.captureMouseDown, true);
    const instance = this.dropdown as unknown as {
      documentClickUnlistener?: EventListener | null;
    };
    if (instance.documentClickUnlistener === this.captureMouseDown) {
      instance.documentClickUnlistener = null;
    }
    this.captureMouseDown = null;
  }

  private handleCaptureMouseDown(event: MouseEvent): void {
    if (!this.dropdown.showList) {
      return;
    }

    const trigger = this.dropdown.el?.nativeElement?.querySelector?.('.ap-dropdown-select');
    const target = event.target;
    if (
      (trigger && target instanceof Node && trigger.contains(target)) ||
      isInsideDropdownPanel(target, this.dropdown.listId)
    ) {
      return;
    }

    this.ngZone.run(() => this.dropdown.onCloseList(event));
  }

  private attachFocusinGuard(): void {
    if (this.focusinGuard) {
      return;
    }
    this.focusinGuard = (event: FocusEvent) => {
      const target = event.target;
      if (
        target instanceof Element &&
        isInsideDropdownPanel(target, this.dropdown.listId)
      ) {
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener('focusin', this.focusinGuard, true);
  }

  private detachFocusinGuard(): void {
    if (!this.focusinGuard) {
      return;
    }
    window.removeEventListener('focusin', this.focusinGuard, true);
    this.focusinGuard = null;
  }
}

function isInsideDropdownPanel(target: EventTarget | null, listId: string): boolean {
  if (!(target instanceof Node)) {
    return false;
  }

  const dropdownList = listId ? document.getElementById(listId) : null;
  if (dropdownList?.contains(target)) {
    return true;
  }

  const overlayPane = dropdownList?.closest('.cdk-overlay-pane');
  if (overlayPane?.contains(target)) {
    return true;
  }

  return target instanceof Element
    ? !!target.closest('.cdk-overlay-container .ap-dropdown-list')
    : false;
}
